import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { formatDateTime, formatNumber } from '../ui/format';
import { Badge, ErrorState, Loading, PageHeader, Panel, type BadgeTone } from '../ui/Page';
import { ConfirmAction } from '../ui/ConfirmAction';
import { BlockList } from './BlockEditor';
import { ExperimentPanel } from './ExperimentPanel';
import { TemplatePicker } from './TemplatePicker';
import {
  addBlock,
  countBlocks,
  moveBlock,
  problemsIn,
  removeBlock,
  setProp,
  type Path,
} from './blocks';
import {
  deletePage,
  getPage,
  listBlocks,
  previewPage,
  publishPage,
  savePage,
  unpublishPage,
  type BlockType,
  type PageContent,
  type PageDetail,
  type SeoFields,
} from './pages-api';

/**
 * Building a landing page.
 *
 * Two halves: the blocks on the left, what the visitor will see on the right. The preview is the
 * server's own renderer rather than a second implementation in the browser — a builder that draws
 * its own approximation of the page is a builder that lies, and the whole point of this screen is
 * to answer "what will this look like" before anybody publishes it.
 *
 * It refreshes when asked, not on every keystroke: a render per character would be a request per
 * character, and the page somebody is typing into is not yet a page worth looking at.
 */

const TONE: Record<string, BadgeTone> = {
  PUBLISHED: 'positive',
  DRAFT: 'neutral',
  ARCHIVED: 'neutral',
};

export function PageBuilderPage(): React.JSX.Element {
  const { pageId = '' } = useParams();
  const client = useQueryClient();
  const navigate = useNavigate();

  const page = useQuery({
    queryKey: ['page', pageId],
    queryFn: () => getPage(pageId),
    enabled: pageId !== '',
  });

  const palette = useQuery({
    queryKey: ['page-blocks'],
    queryFn: listBlocks,
    staleTime: 60 * 60 * 1000,
  });

  const [draft, setDraft] = useState<PageDetail | null>(null);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);

  const version = page.data?.version;
  useEffect(() => {
    if (page.data) {
      setDraft(page.data);
      setDirty(false);
    }
  }, [page.data, version]);

  const save = useMutation({
    mutationFn: (input: Parameters<typeof savePage>[1]) =>
      savePage(pageId, { ...input, expectedVersion: draft?.version }),
    onSuccess: (saved) => {
      setError(null);
      setDraft(saved);
      setDirty(false);
      void client.invalidateQueries({ queryKey: ['pages'] });
      void client.invalidateQueries({ queryKey: ['page', pageId] });
      void client.invalidateQueries({ queryKey: ['page-preview', pageId] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const toggle = useMutation({
    mutationFn: () => (draft?.status === 'PUBLISHED' ? unpublishPage(pageId) : publishPage(pageId)),
    onSuccess: () => {
      setError(null);
      void client.invalidateQueries({ queryKey: ['page', pageId] });
      void client.invalidateQueries({ queryKey: ['pages'] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const drop = useMutation({
    mutationFn: () => deletePage(pageId),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['pages'] });
      navigate('/marketing/paginas');
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  if (page.isLoading || palette.isLoading) return <Loading />;
  if (page.error) return <ErrorState error={page.error} onRetry={() => void page.refetch()} />;
  if (!draft) return <Loading />;

  const blocks = palette.data?.blocks ?? [];
  const problems = problemsIn(draft.content.blocks, blocks);
  const empty = draft.content.blocks.length === 0;

  const edit = (content: PageContent): void => {
    setDraft({ ...draft, content });
    setDirty(true);
  };

  return (
    <div className="view view--wide">
      <PageHeader
        title={draft.name}
        description={t('pages.builderHint')}
        actions={
          <div className="cell-inline">
            <Badge tone={TONE[draft.status] ?? 'neutral'}>{t(`pages.state.${draft.status}`)}</Badge>

            {draft.publicUrl && (
              <a
                className="button button--quiet button--compact"
                href={draft.publicUrl}
                target="_blank"
                rel="noreferrer noopener"
              >
                {t('pages.open')}
              </a>
            )}

            <Link className="button button--quiet button--compact" to="/marketing/paginas">
              {t('record.backToList')}
            </Link>

            <button
              type="button"
              className="button button--compact"
              disabled={!dirty || save.isPending}
              onClick={() =>
                save.mutate({ name: draft.name, seo: draft.seo, content: draft.content })
              }
            >
              {save.isPending ? t('automations.saving') : t('automations.save')}
            </button>

            <button
              type="button"
              className="button button--primary button--compact"
              /* Publishing a draft that does not match the screen would publish a page nobody
                 read. Unpublishing is always allowed: taking something down is never the risk. */
              disabled={
                toggle.isPending ||
                (draft.status !== 'PUBLISHED' && (dirty || empty || problems.length > 0))
              }
              title={
                draft.status !== 'PUBLISHED' && (dirty || empty || problems.length > 0)
                  ? t('pages.fixFirst')
                  : undefined
              }
              onClick={() => toggle.mutate()}
            >
              {draft.status === 'PUBLISHED' ? t('pages.unpublish') : t('pages.publish')}
            </button>
          </div>
        }
      />

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      <div className="record">
        <div className="stack">
          <Panel
            title={`${t('pages.blocks')} · ${countBlocks(draft.content.blocks)}`}
            actions={
              problems.length > 0 ? (
                <Badge tone="warning">
                  {t('automations.problems').replace('{n}', String(problems.length))}
                </Badge>
              ) : undefined
            }
          >
            {empty && (
              <>
                <p className="muted">{t('pages.noBlocks')}</p>
                <TemplatePicker kind="PAGE" onPick={edit} onError={setError} />
              </>
            )}

            <BlockList
              blocks={draft.content.blocks}
              path={[]}
              palette={blocks}
              problems={problems}
              onAdd={(into: Path, type: BlockType) => edit(addBlock(draft.content, into, type))}
              onSet={(at, key, value) => edit(setProp(draft.content, at, key, value))}
              onRemove={(at) => edit(removeBlock(draft.content, at))}
              onMove={(at, direction) => edit(moveBlock(draft.content, at, direction))}
            />
          </Panel>

          <Panel title={t('pages.seo')}>
            <SeoPanel
              seo={draft.seo}
              onChange={(seo) => {
                setDraft({ ...draft, seo });
                setDirty(true);
              }}
            />
          </Panel>

          <Panel title={t('pages.details')}>
            <label className="stack__field">
              <span>{t('crm.field.name')}</span>
              <input
                value={draft.name}
                maxLength={200}
                onChange={(event) => {
                  setDraft({ ...draft, name: event.target.value });
                  setDirty(true);
                }}
              />
            </label>

            <dl className="facts">
              <dt>{t('pages.slug')}</dt>
              <dd>
                <code>/{draft.slug}</code>
              </dd>

              <dt>{t('pages.visits')}</dt>
              <dd>{formatNumber(draft.visitCount)}</dd>

              <dt>{t('pages.conversions')}</dt>
              <dd>{formatNumber(draft.conversionCount)}</dd>
            </dl>

            <button
              type="button"
              className="button button--quiet button--danger button--compact"
              onClick={() => setRemoving(true)}
            >
              {t('social.delete')}
            </button>
          </Panel>

          <ExperimentPanel pageId={pageId} versions={draft.versions} />

          <Panel title={t('automations.versions')}>
            {draft.versions.length === 0 ? (
              <p className="muted small">{t('automations.noVersions')}</p>
            ) : (
              <ul className="home-list">
                {draft.versions.map((entry) => (
                  <li key={entry.id}>
                    <span className="cell-stack">
                      <span className="small">
                        {t('automations.versionN').replace('{n}', String(entry.version))}
                        {entry.variant && ` · ${entry.variant}`}
                        {entry.id === draft.publishedVersionId &&
                          ` · ${t('automations.published')}`}
                      </span>
                      {entry.notes && <span className="muted small">{entry.notes}</span>}
                    </span>
                    <span className="muted small">{formatDateTime(entry.createdAt)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>

        <Preview pageId={pageId} stale={dirty} />
      </div>

      <ConfirmAction
        open={removing}
        title={t('pages.deleteTitle')}
        description={t('pages.deleteHint')}
        confirmLabel={t('social.delete')}
        onCancel={() => setRemoving(false)}
        onConfirm={() => {
          setRemoving(false);
          drop.mutate();
        }}
      />
    </div>
  );
}

/**
 * The page as a visitor will see it, rendered by the server.
 *
 * Sandboxed with nothing allowed: the document is tenant-authored and the block schema forbids
 * script, but a preview pane is a poor place to discover that something got through. `srcdoc`
 * rather than `src` because the session is a bearer token an iframe cannot send.
 */
function Preview({ pageId, stale }: { pageId: string; stale: boolean }): React.JSX.Element {
  const preview = useQuery({
    queryKey: ['page-preview', pageId],
    queryFn: () => previewPage(pageId),
  });

  return (
    <Panel
      title={t('pages.preview')}
      actions={
        <div className="cell-inline">
          {stale && <span className="muted small">{t('pages.previewStale')}</span>}
          <button
            type="button"
            className="button button--quiet button--compact"
            disabled={preview.isFetching}
            onClick={() => void preview.refetch()}
          >
            {t('pages.refresh')}
          </button>
        </div>
      }
    >
      {preview.isLoading ? (
        <Loading />
      ) : preview.error ? (
        <ErrorState error={preview.error} onRetry={() => void preview.refetch()} />
      ) : (
        <iframe
          className="page-preview"
          title={t('pages.preview')}
          sandbox=""
          srcDoc={preview.data ?? ''}
        />
      )}
    </Panel>
  );
}

/** What search engines and a shared link will say. Every field optional; the title falls back. */
function SeoPanel({
  seo,
  onChange,
}: {
  seo: SeoFields;
  onChange: (seo: SeoFields) => void;
}): React.JSX.Element {
  const fields: { key: keyof SeoFields; rows?: number }[] = [
    { key: 'title' },
    { key: 'description', rows: 2 },
    { key: 'ogTitle' },
    { key: 'ogDescription', rows: 2 },
    { key: 'ogImage' },
    { key: 'canonical' },
  ];

  return (
    <div className="stack">
      {fields.map((field) => (
        <label key={field.key} className="stack__field">
          <span>{t(`pages.seo.${field.key}`)}</span>
          {field.rows ? (
            <textarea
              rows={field.rows}
              value={seo[field.key] ?? ''}
              maxLength={500}
              onChange={(event) => onChange({ ...seo, [field.key]: event.target.value })}
            />
          ) : (
            <input
              value={seo[field.key] ?? ''}
              maxLength={2000}
              onChange={(event) => onChange({ ...seo, [field.key]: event.target.value })}
            />
          )}
        </label>
      ))}

      <label className="cell-inline">
        <input
          type="checkbox"
          checked={seo.robots === 'noindex'}
          onChange={(event) => onChange({ ...seo, robots: event.target.checked ? 'noindex' : '' })}
        />
        <span className="muted small">{t('pages.seo.noindex')}</span>
      </label>
    </div>
  );
}
